/**
 * Éditeur de carte de l'organisation : chemins internes (sentiers, allées, voies de service…)
 * et équipements (douches, toilettes, eau…).
 *
 * Façon Google My Maps / éditeur iD : on choisit un outil, on clique sur la carte.
 * - Tracer : un clic par point, double-clic ou Entrée pour terminer, Retour arrière pour enlever
 *   le dernier point, Échap pour annuler. Les points s'accrochent aux extrémités et aux points
 *   des autres chemins (jonctions).
 * - Modifier : glisser un point, cliquer sur un « + » pour en ajouter un, choisir un point pour
 *   le supprimer ou couper le chemin en deux à cet endroit.
 * Rien n'est enregistré sans « Enregistrer » ; Ctrl+Z annule la dernière modification du tracé.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Layer, Marker, Source, type MapRef } from '@vis.gl/react-maplibre'
import type { MapLayerMouseEvent } from 'maplibre-gl'
import { BaseMap } from '@web/features/map/components/BaseMap'
import { MartinTrailLayers } from '@web/features/map/components/MartinTrailLayers'
import { DEFAULT_TRAIL_TYPE_SELECTION, type MapTrailTypeSelection } from '@web/features/map/utils/mapTrailTypes'
import { MARTIN_TRAILS_SOURCE_ID } from '@web/features/map/utils/martinTrailLayers'
import { useOrganizationLocations } from '@web/features/organizations/hooks/useOrganizationLocations'
import { usePOICategories } from '@web/features/map/hooks/usePOICategories'
import type { POICategory } from '@web/features/map/services/mapApi'
import {
  createFacility,
  createPath,
  deleteFacility,
  deletePath,
  createPoi,
  deletePoi,
  facilityToPoi,
  fetchEditorBundle,
  fetchLinkedTrails,
  fetchPois,
  poiToFacility,
  updatePoi,
  linkTrail,
  lookupTrail,
  unlinkTrail,
  updateFacility,
  updatePath,
  type EditorBundle,
  type LatLng,
  type OrgFacility,
  type OrgPath,
  type PathAccess,
  type OrgPoi,
  type TrailFeature,
} from '../api'
import { PathFields, type PathForm } from '../components/map/PathFields'
import { TrailPanel } from '../components/map/TrailPanel'
import { GpxImportCard, GpxPreviewLayers, readGpxFile, type GpxDraft } from '../components/map/GpxImport'
import { PoiPalette, defaultPlaceAs, poiIcon, rememberPlaceAs, rememberPoiType, type PlaceAs, type PlaceKind } from '../components/map/PoiPalette'
import { NewPlacePanel, PlacePanel } from '../components/map/PlacePanel'
import { PoiPanel } from '../components/map/PoiPanel'
import { ClimbingEditLayers } from '../components/map/climbing/ClimbingEditLayers'
import { ClimbingPanel } from '../components/map/climbing/ClimbingPanel'
import { useClimbingEditor } from '../components/map/climbing/useClimbingEditor'
import { KindSwitch } from '../components/map/KindSwitch'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { createOrganizationLocation, fetchOrganizationPitches } from '@web/features/organizations/services/organizationApi'
import type { OrganizationLocation } from '@web/features/organizations/types'
import { TypeIcon } from '../components/badges'
import { useOrg } from '../utils/orgContext'
import { editLocationPath } from '../utils/routes'
import {
  boundsOf,
  formatLength,
  geometryPoints,
  junctionCandidates,
  junctionKeys,
  lengthM,
  lineCoords,
  midpoint,
  pointKey,
  sliceBetween,
  toLngLat,
  type JunctionCandidate,
} from '../map/geo'

type Mode = 'select' | 'draw' | 'facility' | 'cut'
type Selection =
  | { kind: 'path'; id: number | 'new' }
  | { kind: 'facility'; id: number | 'new' }
  | { kind: 'trail'; feature: TrailFeature }
  | { kind: 'poi'; id: number }
  | { kind: 'place'; place: OrganizationLocation }
  | { kind: 'newPlace'; type: PlaceKind; at: LatLng }
  | null
type FacilityForm = { facility_type: string; poi_type_id: number | null; name: string; info_text: string; access: PathAccess; notes: string }

const PATH_COLORS: Record<string, string> = {
  road: '#37474f',
  track: '#8d6e63',
  trail: '#2e7d32',
  walk: '#a1887f',
  service: '#9e9e9e',
  access: '#5d4037',
  bike: '#1565c0',
  slow: '#f9a825',
  vehicle: '#455a64',
}
/** L'accès choisi pour le dernier chemin devient celui par défaut du suivant. */
const ACCESS_KEY = 'gestion.carte.acces'

function defaultAccess(): PathAccess {
  try {
    return localStorage.getItem(ACCESS_KEY) === 'private' ? 'private' : 'public'
  } catch {
    return 'public'
  }
}

function rememberAccess(access: PathAccess) {
  try {
    localStorage.setItem(ACCESS_KEY, access)
  } catch {
    // stockage indisponible : on garde « public » par défaut
  }
}
const SNAP_PX = 12
const ALL_TRAILS: MapTrailTypeSelection = Object.fromEntries(
  Object.keys(DEFAULT_TRAIL_TYPE_SELECTION).map((k) => [k, true]),
) as MapTrailTypeSelection

const pathColor = (type: string) => PATH_COLORS[type] ?? '#6d4c41'
const LINKED_COLOR = '#954a00'
/** Copie d'un sentier OSM : ses types deviennent les utilisations du chemin. */
const TRAIL_TYPE_TO_USAGE: Record<string, string> = {
  hiking_major: 'hiking',
  hiking_standard: 'hiking',
  hiking_minor: 'hiking',
  hiking: 'hiking',
  snowshoe: 'snowshoe',
  cycling: 'cycling',
  mtb: 'mtb',
  atv: 'atv',
  snowmobile: 'snowmobile',
}
const PLACE_TYPES = new Set(['camping', 'refuge', 'accommodation'])

function pathFormOf(p?: OrgPath | null): PathForm {
  return {
    path_type: p?.path_type ?? 'trail',
    access: p?.access ?? defaultAccess(),
    display: p?.display ?? 'organization',
    osm_sync_requested: p?.osm_sync_requested ?? false,
    usages: p?.usages ?? [],
    name: p?.name ?? '',
    notes: p?.notes ?? '',
    speed_limit_kmh: p?.speed_limit_kmh != null ? String(p.speed_limit_kmh) : '',
  }
}

function facilityFormOf(f: OrgFacility | null | undefined, defaultType: string): FacilityForm {
  return {
    facility_type: f?.facility_type ?? defaultType,
    poi_type_id: f?.poi_type_id ?? f?.poi_type?.id ?? null,
    name: f?.name ?? '',
    info_text: f?.info_text ?? '',
    access: f?.access ?? defaultAccess(),
    notes: f?.notes ?? '',
  }
}

export function CartePage() {
  const { t } = useTranslation('gestion')
  const { orgId } = useOrg()
  const queryClient = useQueryClient()
  const mapRef = useRef<MapRef>(null)
  const { data: bundle, isLoading, error } = useQuery({
    queryKey: ['editorBundle', orgId],
    queryFn: ({ signal }) => fetchEditorBundle(orgId, signal),
  })

  const [paths, setPaths] = useState<OrgPath[]>([])
  const [facilities, setFacilities] = useState<OrgFacility[]>([])
  useEffect(() => {
    if (!bundle) return
    setPaths(bundle.paths)
    setFacilities(bundle.facilities)
  }, [bundle])

  const [mode, setMode] = useState<Mode>('select')
  const [draft, setDraft] = useState<LatLng[]>([])
  /** « Tracer un chemin » depuis un GPX : fichier lu, en attente de simplification. */
  const [gpx, setGpx] = useState<GpxDraft | null>(null)
  const gpxInput = useRef<HTMLInputElement>(null)
  const [selection, setSelection] = useState<Selection>(null)
  // Tracé en cours de modification du chemin choisi, et l'historique pour Ctrl+Z.
  const [geom, setGeom] = useState<LatLng[]>([])
  const [history, setHistory] = useState<LatLng[][]>([])
  const [activeVertex, setActiveVertex] = useState<number | null>(null)
  const [pathForm, setPathForm] = useState<PathForm>(pathFormOf())
  const [facilityForm, setFacilityForm] = useState<FacilityForm>({ facility_type: '', poi_type_id: null, name: '', info_text: '', access: 'public', notes: '' })
  const [facilityPoint, setFacilityPoint] = useState<LatLng | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string; undo?: () => void } | null>(null)
  const [placeType, setPlaceType] = useState<POICategory | null>(null)
  const [placeKind, setPlaceKind] = useState<PlaceKind | null>(null)
  const [placeAs, setPlaceAsState] = useState<PlaceAs>(defaultPlaceAs)
  const setPlaceAs = (v: PlaceAs) => {
    setPlaceAsState(v)
    rememberPlaceAs(v)
  }
  const navigate = useNavigate()
  const { data: poiCategories } = usePOICategories()
  const [showPublicTrails, setShowPublicTrails] = useState(true)
  const [satellite, setSatellite] = useState(false)
  const [layersOpen, setLayersOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [cutPoints, setCutPoints] = useState<LatLng[]>([])
  const [trailBusy, setTrailBusy] = useState(false)
  const { data: linkedTrails } = useQuery({
    queryKey: ['linkedTrails', orgId],
    queryFn: ({ signal }) => fetchLinkedTrails(orgId, signal),
  })
  const { data: places = [] } = useOrganizationLocations(orgId)
  // Emplacements de camping : affichés pour repère ; ils se gèrent dans Refuges et campings → Emplacements.
  const { data: pitchesData } = useQuery({ queryKey: ['orgPitches', orgId], queryFn: ({ signal }) => fetchOrganizationPitches(orgId, { signal }) })
  const pitches = (pitchesData?.results ?? []).filter((p) => p.location.lat != null && p.location.lng != null)
  const { data: poisData } = useQuery({ queryKey: ['orgPois', orgId], queryFn: ({ signal }) => fetchPois(orgId, signal) })
  const pois = poisData?.results ?? []
  // POI « Escalade » choisi : onglets Fiche / Parois / Voies, reliés au même site d'escalade.
  const selectedPoi = selection?.kind === 'poi' ? pois.find((x) => x.id === selection.id) : undefined
  const climbing = useClimbingEditor(orgId, selectedPoi?.poi_type?.slug === 'climbing' ? selectedPoi.id : null)
  const refreshPois = () => queryClient.invalidateQueries({ queryKey: ['orgPois', orgId] })
  const refreshPlaces = () => {
    void queryClient.invalidateQueries({ queryKey: ['organization', orgId, 'locations'] })
    void queryClient.invalidateQueries({ queryKey: ['insights', orgId] })
  }

  const defaultFacilityType = bundle?.facility_types[0]?.value ?? 'other'
  const selectedPath = selection?.kind === 'path' && selection.id !== 'new' ? paths.find((p) => p.id === selection.id) : null
  const selectedFacility =
    selection?.kind === 'facility' && selection.id !== 'new' ? facilities.find((f) => f.id === selection.id) : null

  const flash = (kind: 'ok' | 'error', text: string, undo?: () => void) => {
    setMessage({ kind, text, undo })
    window.setTimeout(() => setMessage((m) => (m?.text === text ? null : m)), undo ? 10000 : 4000)
  }

  const confirmDiscard = useCallback(() => !dirty || window.confirm(t('carte.abandonner_modifs')), [dirty, t])

  /* ---------- Cadrage initial ---------- */
  const fitted = useRef(false)
  const fitToContent = useCallback(
    (b: EditorBundle | undefined) => {
      const map = mapRef.current
      if (!map || !b) return
      const pts = [
        ...geometryPoints(b.geometry?.boundary ?? null),
        ...b.paths.flatMap((p) => p.coordinates),
        ...b.facilities.flatMap((f) => (f.location.lat != null && f.location.lng != null ? [{ lat: f.location.lat, lng: f.location.lng }] : [])),
      ]
      const bounds = boundsOf(pts)
      if (bounds && pts.length > 1) map.fitBounds(bounds, { padding: 60, maxZoom: 17, duration: 0 })
      else {
        const center = pts[0] ?? geometryPoints(b.geometry?.point ?? null)[0]
        if (center) map.jumpTo({ center: toLngLat(center), zoom: 15 })
      }
    },
    [],
  )
  useEffect(() => {
    if (bundle && mapRef.current && !fitted.current) {
      fitted.current = true
      fitToContent(bundle)
    }
  }, [bundle, fitToContent])

  /* ---------- Accrochage aux points existants ---------- */
  const snap = useCallback(
    (p: LatLng, opts: { excludePathId?: number | 'new'; extra?: LatLng[] } = {}): LatLng => {
      const map = mapRef.current
      if (!map) return p
      const candidates: LatLng[] = [...(opts.extra ?? [])]
      for (const path of paths) if (path.id !== opts.excludePathId) candidates.push(...path.coordinates)
      const target = map.project(toLngLat(p))
      let best: LatLng | null = null
      let bestD = SNAP_PX
      for (const c of candidates) {
        const q = map.project(toLngLat(c))
        const d = Math.hypot(q.x - target.x, q.y - target.y)
        if (d < bestD) {
          bestD = d
          best = c
        }
      }
      return best ? { ...best } : p
    },
    [paths],
  )

  /* ---------- Sélection ---------- */
  const selectPath = (p: OrgPath) => {
    if (!confirmDiscard()) return
    setMode('select')
    setSelection({ kind: 'path', id: p.id })
    setGeom(p.coordinates)
    setHistory([])
    setActiveVertex(null)
    setPathForm(pathFormOf(p))
    setDirty(false)
  }
  const selectFacility = (f: OrgFacility) => {
    if (!confirmDiscard()) return
    setMode('select')
    setSelection({ kind: 'facility', id: f.id })
    setFacilityForm(facilityFormOf(f, defaultFacilityType))
    setFacilityPoint(f.location.lat != null && f.location.lng != null ? { lat: f.location.lat, lng: f.location.lng } : null)
    setDirty(false)
  }
  const clearSelection = () => {
    setSelection(null)
    setCutPoints([])
    setGeom([])
    setHistory([])
    setActiveVertex(null)
    setFacilityPoint(null)
    setDirty(false)
  }
  const flyTo = (pts: LatLng[]) => {
    const b = boundsOf(pts)
    if (b) mapRef.current?.fitBounds(b, { padding: 120, maxZoom: 18, duration: 500 })
  }

  const changeGeom = (next: LatLng[]) => {
    setHistory((h) => [...h.slice(-49), geom])
    setGeom(next)
    setDirty(true)
  }
  const undo = useCallback(() => {
    if (mode === 'draw') {
      setDraft((d) => d.slice(0, -1))
      return
    }
    setHistory((h) => {
      if (!h.length) return h
      setGeom(h[h.length - 1])
      setActiveVertex(null)
      return h.slice(0, -1)
    })
  }, [mode])

  /* ---------- Outils ---------- */
  const startTool = (next: Mode) => {
    if (!confirmDiscard()) return
    clearSelection()
    setDraft([])
    setGpx(null)
    if (next !== 'facility') {
      setPlaceType(null)
      setPlaceKind(null)
    }
    setMode(next)
  }
  // Liens depuis les autres pages : ?equipement=<id> ou ?poi=<id> ouvre ce point, ?emplacement=<id> y centre la
  // carte, ?outil=equipement ouvre la palette.
  const [searchParams, setSearchParams] = useSearchParams()
  const pendingFocus = useRef<LatLng[] | null>(null)
  useEffect(() => {
    if (!bundle) return
    const facilityId = Number(searchParams.get('equipement'))
    const poiId = Number(searchParams.get('poi'))
    const pitchId = Number(searchParams.get('emplacement'))
    const tool = searchParams.get('outil')
    if (!facilityId && !poiId && !pitchId && !tool) return
    if (poiId && !poisData) return // attendre la liste des POI
    if (pitchId && !pitchesData) return
    const pitch = pitchId ? pitches.find((x) => x.id === pitchId) : undefined
    const f = facilityId ? facilities.find((x) => x.id === facilityId) : undefined
    const poi = poiId ? pois.find((x) => x.id === poiId) : undefined
    if (pitch) {
      fitted.current = true
      const pts = [{ lat: pitch.location.lat!, lng: pitch.location.lng! }]
      if (mapRef.current) flyTo(pts)
      else pendingFocus.current = pts
    } else if (poi?.location) {
      fitted.current = true
      setSelection({ kind: 'poi', id: poi.id })
      if (mapRef.current) flyTo([poi.location])
      else pendingFocus.current = [poi.location]
    } else if (f && f.location.lat != null && f.location.lng != null) {
      fitted.current = true
      selectFacility(f)
      const pts = [{ lat: f.location.lat, lng: f.location.lng }]
      if (mapRef.current) flyTo(pts)
      else pendingFocus.current = pts // la carte n'est pas encore chargée : au onLoad
    } else if (tool === 'equipement') {
      startTool('facility')
    }
    setSearchParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- une fois, quand les données arrivent
  }, [bundle, facilities, poisData, pitchesData])

  const finishDraft = useCallback(() => {
    if (draft.length < 2) {
      setDraft([])
      setMode('select')
      return
    }
    setSelection({ kind: 'path', id: 'new' })
    setGeom(draft)
    setHistory([])
    setPathForm(pathFormOf())
    setDirty(true)
    setDraft([])
    setMode('select')
  }, [draft])
  const openGpx = async (file: File | undefined) => {
    if (!file) return
    try {
      const read = await readGpxFile(file)
      setDraft([])
      setGpx(read)
      flyTo(read.segments[read.segment].map(([lng, lat]) => ({ lat, lng })))
    } catch {
      flash('error', t('carte.gpx_illisible'))
    }
  }
  /** Le tracé simplifié devient un nouveau chemin, comme un tracé fait à la main (nom du GPX repris). */
  const applyGpx = (points: LatLng[], name: string) => {
    setGpx(null)
    setSelection({ kind: 'path', id: 'new' })
    setGeom(points)
    setHistory([])
    setPathForm({ ...pathFormOf(), name })
    setDirty(true)
    setDraft([])
    setMode('select')
  }
  const cancelAll = useCallback(() => {
    if (mode !== 'select') {
      setDraft([])
      setGpx(null)
      setCutPoints([])
      setPlaceType(null)
      setPlaceKind(null)
      setMode('select')
      return
    }
    if (confirmDiscard()) clearSelection()
  }, [mode, confirmDiscard])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (e.key === 'Escape') cancelAll()
      else if (e.key === 'Enter' && mode === 'draw') finishDraft()
      else if ((e.key === 'Backspace' || e.key === 'Delete') && mode === 'draw') setDraft((d) => d.slice(0, -1))
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        undo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, finishDraft, cancelAll, undo])

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  /* ---------- Clics sur la carte ---------- */
  const onMapClick = (e: MapLayerMouseEvent) => {
    const p = { lat: e.lngLat.lat, lng: e.lngLat.lng }
    if (climbing.handleMapClick(p)) return
    if (gpx) return
    if (mode === 'draw') {
      setDraft((d) => [...d, snap(p, { extra: d.slice(0, 1) })])
      return
    }
    if (mode === 'facility') {
      if (placeKind) {
        setSelection({ kind: 'newPlace', type: placeKind, at: p })
        setPlaceKind(null)
        setMode('select')
        return
      }
      if (!placeType) {
        flash('error', t('carte.choisir_type_dabord'))
        return
      }
      void placeFacility(placeType, p)
      return
    }
    if (mode === 'cut' && selection?.kind === 'trail') {
      const next = [...cutPoints, p]
      if (next.length < 2) {
        setCutPoints(next)
        return
      }
      const part = sliceBetween(lineCoords(selection.feature.geometry), next[0], next[1])
      setCutPoints([])
      setMode('select')
      if (part.length >= 2) copyTrail(selection.feature, part)
      return
    }
    const hit = e.features?.find((f) => f.layer.id === 'org-paths-hit')
    if (hit) {
      const p2 = paths.find((x) => x.id === Number(hit.properties?.id))
      if (p2 && p2.id !== (selection?.kind === 'path' ? selection.id : null)) selectPath(p2)
      return
    }
    const trailHit = networkTrailAt(e.point)
    if (trailHit) {
      void openTrail(trailHit)
      return
    }
    if (selection && !dirty) clearSelection()
  }

  /* ---------- Sentiers du réseau public ---------- */
  /** Sentier du réseau sous le clic : sentiers associés d'abord, puis tuiles Martin (à 6 px près). */
  const networkTrailAt = (point: { x: number; y: number }): { osmId: number | null; trailId: number | null } | null => {
    const map = mapRef.current?.getMap()
    if (!map) return null
    const box: [[number, number], [number, number]] = [
      [point.x - 6, point.y - 6],
      [point.x + 6, point.y + 6],
    ]
    const features = map.queryRenderedFeatures(box)
    const linked = features.find((f) => f.layer.id === 'org-linked-hit')
    const martin = features.find((f) => f.source === MARTIN_TRAILS_SOURCE_ID)
    const f = linked ?? martin
    if (!f) return null
    const osm = Number(f.properties?.osm_id)
    const id = Number(f.properties?.id)
    return { osmId: Number.isFinite(osm) && osm > 0 ? osm : null, trailId: Number.isFinite(id) ? id : null }
  }

  const openTrail = async (ref: { osmId?: number | null; trailId?: number | null }) => {
    if (!confirmDiscard()) return
    try {
      const feature = await lookupTrail(orgId, ref)
      clearSelection()
      setMode('select')
      setSelection({ kind: 'trail', feature })
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    }
  }

  const setTrailLink = async (link: boolean) => {
    if (selection?.kind !== 'trail') return
    const feature = selection.feature
    setTrailBusy(true)
    try {
      if (link) await linkTrail(orgId, feature.properties.id)
      else await unlinkTrail(orgId, feature.properties.id)
      setSelection({ kind: 'trail', feature: { ...feature, properties: { ...feature.properties, linked: link } } })
      void queryClient.invalidateQueries({ queryKey: ['linkedTrails', orgId] })
      flash('ok', link ? t('carte.associe_ok') : t('carte.retire_ok'))
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    } finally {
      setTrailBusy(false)
    }
  }

  /** Copie (tout ou partie d'un sentier du réseau) dans un nouveau chemin de l'organisation, à enregistrer. */
  const copyTrail = (feature: TrailFeature, coords: LatLng[]) => {
    const usages = [...new Set(feature.properties.trail_types.map((k) => TRAIL_TYPE_TO_USAGE[k]).filter(Boolean))]
    setSelection({ kind: 'path', id: 'new' })
    setGeom(coords)
    setHistory([])
    setActiveVertex(null)
    setPathForm({ ...pathFormOf(), name: feature.properties.name, usages })
    setDirty(true)
    flash('ok', t('carte.copie_prete'))
  }

  /* ---------- Enregistrer, couper, supprimer ---------- */
  const refreshBundle = () => queryClient.invalidateQueries({ queryKey: ['editorBundle', orgId] })

  const pathPayload = () => ({
    path_type: pathForm.path_type,
    access: pathForm.access,
    display: pathForm.display,
    osm_sync_requested: pathForm.osm_sync_requested,
    usages: pathForm.usages,
    name: pathForm.name.trim() || null,
    notes: pathForm.notes.trim() || null,
    speed_limit_kmh: pathForm.speed_limit_kmh ? Number(pathForm.speed_limit_kmh) : null,
  })

  const savePath = async () => {
    if (geom.length < 2) return
    setSaving(true)
    try {
      const saved =
        selection?.kind === 'path' && selection.id !== 'new'
          ? await updatePath(orgId, selection.id, { ...pathPayload(), coordinates: geom })
          : await createPath(orgId, { ...pathPayload(), coordinates: geom })
      setPaths((list) => [...list.filter((x) => x.id !== saved.id), saved])
      rememberAccess(saved.access)
      setSelection({ kind: 'path', id: saved.id })
      setGeom(saved.coordinates)
      setHistory([])
      setDirty(false)
      flash('ok', t('carte.enregistre'))
      void refreshBundle()
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const splitAt = async (index: number) => {
    if (selection?.kind !== 'path' || selection.id === 'new' || index <= 0 || index >= geom.length - 1) return
    if (!window.confirm(t('carte.confirmer_couper'))) return
    setSaving(true)
    try {
      const first = await updatePath(orgId, selection.id, { ...pathPayload(), coordinates: geom.slice(0, index + 1) })
      const second = await createPath(orgId, { ...pathPayload(), coordinates: geom.slice(index) })
      setPaths((list) => [...list.filter((x) => x.id !== first.id), first, second])
      setSelection({ kind: 'path', id: second.id })
      setGeom(second.coordinates)
      setHistory([])
      setActiveVertex(null)
      setPathForm(pathFormOf(second))
      setDirty(false)
      flash('ok', t('carte.coupe'))
      void refreshBundle()
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const removePath = async () => {
    if (selection?.kind !== 'path') return
    if (selection.id === 'new') return clearSelection()
    if (!window.confirm(t('carte.confirmer_supprimer_chemin'))) return
    setSaving(true)
    try {
      await deletePath(orgId, selection.id)
      const id = selection.id
      setPaths((list) => list.filter((x) => x.id !== id))
      clearSelection()
      flash('ok', t('carte.supprime'))
      void refreshBundle()
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const saveFacility = async () => {
    if (!facilityPoint) return
    setSaving(true)
    try {
      const data = {
        ...(facilityForm.poi_type_id ? { poi_type_id: facilityForm.poi_type_id } : { facility_type: facilityForm.facility_type }),
        name: facilityForm.name.trim() || null,
        info_text: facilityForm.info_text.trim() || null,
        access: facilityForm.access,
        notes: facilityForm.notes.trim() || null,
        location: facilityPoint,
      }
      const saved =
        selection?.kind === 'facility' && selection.id !== 'new'
          ? await updateFacility(orgId, selection.id, data)
          : await createFacility(orgId, data)
      setFacilities((list) => [...list.filter((x) => x.id !== saved.id), saved])
      if (saved.access) rememberAccess(saved.access)
      setSelection({ kind: 'facility', id: saved.id })
      setDirty(false)
      flash('ok', t('carte.enregistre'))
      void refreshBundle()
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  /** Placement en deux clics : type dans la palette, puis la carte. « Annuler » retire le repère. */
  const placeFacility = async (type: POICategory, at: LatLng) => {
    if (placeAs === 'poi') {
      try {
        const created = await createPoi(orgId, { poi_type_id: type.id, location: at })
        rememberPoiType(type.slug)
        void refreshPois()
        flash('ok', t('carte.poi_place', { name: type.name }), () => {
          void deletePoi(orgId, created.id).then(() => {
            void refreshPois()
            setMessage(null)
          })
        })
      } catch (err) {
        flash('error', err instanceof Error ? err.message : String(err))
      }
      return
    }
    try {
      const created = await createFacility(orgId, { poi_type_id: type.id, access: defaultAccess(), location: at })
      setFacilities((list) => [...list, created])
      rememberPoiType(type.slug)
      flash('ok', t('carte.equipement_place', { name: type.name }), () => {
        void deleteFacility(orgId, created.id).then(() => {
          setFacilities((list) => list.filter((x) => x.id !== created.id))
          setMessage(null)
        })
      })
      void refreshBundle()
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    }
  }

  /**
   * « Ajouter un point de jonction ici » : le point est ajouté au chemin en cours (à enregistrer)
   * et, s'il le faut, tout de suite à l'autre chemin, pour que les deux soient reliés.
   */
  const addJunction = async (c: JunctionCandidate) => {
    const other = paths.find((p) => p.id === c.otherId)
    if (!other) return
    if (c.kind === 'otherEnd') {
      changeGeom([...geom.slice(0, c.seg + 1), c.point, ...geom.slice(c.seg + 1)])
      flash('ok', t('carte.jonction_ajoutee'))
      return
    }
    if (c.kind === 'cross') changeGeom([...geom.slice(0, c.seg + 1), c.point, ...geom.slice(c.seg + 1)])
    else changeGeom(geom.map((x, i) => (i === c.end ? c.point : x)))
    try {
      const coords = [...other.coordinates.slice(0, c.otherSeg + 1), c.point, ...other.coordinates.slice(c.otherSeg + 1)]
      const saved = await updatePath(orgId, other.id, { coordinates: coords })
      setPaths((list) => list.map((p) => (p.id === saved.id ? saved : p)))
      flash('ok', t('carte.jonction_ajoutee'))
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    }
  }

  const runBusy = async (action: () => Promise<void>) => {
    setSaving(true)
    try {
      await action()
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const savePoi = (id: number, data: { name: string; poi_type_id: number; website: string | null; osm_sync_requested: boolean }) =>
    runBusy(async () => {
      await updatePoi(orgId, id, data)
      await refreshPois()
      flash('ok', t('carte.enregistre'))
    })

  const convertPoiToFacility = (id: number) =>
    runBusy(async () => {
      if (!window.confirm(t('carte.confirmer_poi_vers_equipement'))) return
      const facility = await poiToFacility(orgId, id)
      setFacilities((list) => [...list, facility])
      await refreshPois()
      clearSelection()
      selectFacility(facility)
      flash('ok', t('carte.converti_equipement'))
      void refreshBundle()
    })

  const removePoi = (id: number) =>
    runBusy(async () => {
      if (!window.confirm(t('carte.confirmer_supprimer_poi'))) return
      await deletePoi(orgId, id)
      await refreshPois()
      clearSelection()
      flash('ok', t('carte.supprime'))
    })

  const convertFacilityToPoi = () =>
    runBusy(async () => {
      if (selection?.kind !== 'facility' || selection.id === 'new') return
      if (!window.confirm(t('carte.confirmer_equipement_vers_poi'))) return
      const id = selection.id
      const poi = await facilityToPoi(orgId, id)
      setFacilities((list) => list.filter((x) => x.id !== id))
      await refreshPois()
      clearSelection()
      setSelection({ kind: 'poi', id: poi.id })
      flash('ok', t('carte.converti_poi'))
      void refreshBundle()
    })

  const createPlace = (kind: PlaceKind, at: LatLng, name: string, openEditor: boolean) =>
    runBusy(async () => {
      const created = await createOrganizationLocation(orgId, { name, type: kind, coordinates: at })
      refreshPlaces()
      if (openEditor) {
        navigate(editLocationPath(orgId, kind, created.id))
        return
      }
      clearSelection()
      flash('ok', t('carte.lieu_cree', { name }))
    })

  const removeFacility = async () => {
    if (selection?.kind !== 'facility') return
    if (selection.id === 'new') return clearSelection()
    if (!window.confirm(t('carte.confirmer_supprimer_equipement'))) return
    setSaving(true)
    try {
      const id = selection.id
      await deleteFacility(orgId, id)
      setFacilities((list) => list.filter((x) => x.id !== id))
      clearSelection()
      flash('ok', t('carte.supprime'))
      void refreshBundle()
    } catch (err) {
      flash('error', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  /* ---------- Données de la carte ---------- */
  const selectedId = selection?.kind === 'path' ? selection.id : null
  const pathsGeoJson = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: paths
        .filter((p) => p.id !== selectedId && p.coordinates.length > 1)
        .map((p) => ({
          type: 'Feature',
          properties: { id: p.id, color: pathColor(p.path_type), dashed: p.access === 'private' },
          geometry: { type: 'LineString', coordinates: p.coordinates.map(toLngLat) },
        })),
    }),
    [paths, selectedId],
  )
  const editGeoJson = useMemo<GeoJSON.FeatureCollection>(() => {
    const line = selection?.kind === 'path' ? geom : draft
    return {
      type: 'FeatureCollection',
      features:
        line.length > 1
          ? [{ type: 'Feature', properties: { color: pathColor(selection?.kind === 'path' ? pathForm.path_type : 'trail') }, geometry: { type: 'LineString', coordinates: line.map(toLngLat) } }]
          : [],
    }
  }, [selection, geom, draft, pathForm.path_type])
  /** Jonctions (points communs à plusieurs chemins), avec le chemin en cours tel qu'il est modifié. */
  const junctions = useMemo(() => {
    const lines = paths.filter((p) => p.id !== selectedId).map((p) => p.coordinates)
    if (selection?.kind === 'path') lines.push(geom)
    return junctionKeys(lines)
  }, [paths, selectedId, selection, geom])

  /** Extrémités et jonctions de tous les chemins (sauf celui en cours, qui a ses poignées). */
  const nodesGeoJson = useMemo<GeoJSON.FeatureCollection>(() => {
    const features: GeoJSON.Feature[] = []
    const seen = new Set<string>()
    for (const p of paths) {
      if (p.id === selectedId || p.coordinates.length < 2) continue
      p.coordinates.forEach((c, i) => {
        const k = pointKey(c)
        const end = i === 0 || i === p.coordinates.length - 1
        const junction = junctions.has(k)
        const editing = selection?.kind === 'path' || mode === 'draw'
        if (seen.has(k) || (!end && !junction && !editing)) return
        seen.add(k)
        features.push({
          type: 'Feature',
          properties: { role: junction ? 'junction' : end ? 'end' : 'vertex' },
          geometry: { type: 'Point', coordinates: toLngLat(c) },
        })
      })
    }
    return { type: 'FeatureCollection', features }
  }, [paths, selectedId, junctions, selection, mode])

  const candidates = useMemo<JunctionCandidate[]>(
    () => (selection?.kind === 'path' ? junctionCandidates(geom, paths.filter((p) => p.id !== selectedId)) : []),
    [selection, geom, paths, selectedId],
  )

  const draftPoints = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: draft.map((c, i) => ({ type: 'Feature', properties: { first: i === 0 }, geometry: { type: 'Point', coordinates: toLngLat(c) } })),
    }),
    [draft],
  )
  const boundary = bundle?.geometry?.boundary ?? null

  const visiblePaths = paths
    .filter((p) => !filter.trim() || (p.name ?? p.path_type_label).toLowerCase().includes(filter.trim().toLowerCase()))
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  const visibleLinked = (linkedTrails?.features ?? []).filter(
    (f) => !filter.trim() || (f.properties.name ?? '').toLowerCase().includes(filter.trim().toLowerCase()),
  )
  const selectedTrailGeoJson = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features:
        selection?.kind === 'trail' && selection.feature.geometry
          ? [{ type: 'Feature', geometry: selection.feature.geometry, properties: {} }]
          : [],
    }),
    [selection],
  )
  const orgPoiCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const f of facilities) if (f.poi_type?.slug) counts[f.poi_type.slug] = (counts[f.poi_type.slug] ?? 0) + 1
    return counts
  }, [facilities])
  const visibleFacilities = facilities.filter(
    (f) => !filter.trim() || `${f.name ?? ''} ${f.type_label ?? f.facility_type_label}`.toLowerCase().includes(filter.trim().toLowerCase()),
  )

  const pathTypes = bundle?.path_types ?? []
  const pathUsages = bundle?.path_usages ?? []
  const usageLabel = (value: string) => pathUsages.find((x) => x.value === value)?.label ?? value
  const facilityTypes = bundle?.facility_types ?? []
  const typeLabel = (value: string) => pathTypes.find((x) => x.value === value)?.label ?? value

  return (
    <div className={`g-editor g-editor--${mode}`}>
      <aside className="g-editor__panel">
        <div className="g-editor__panel-head">
          <h1 className="g-page__title g-page__title--sm">{t('carte.titre')}</h1>
          <p className="g-muted">{t('carte.intro')}</p>
        </div>

        {selection === null && (
          <>
            <input className="g-input" type="search" placeholder={t('carte.filtrer')} value={filter} onChange={(e) => setFilter(e.target.value)} />
            <h2 className="g-editor__section">
              {t('carte.chemins')} <span className="g-chip__count">{paths.length}</span>
            </h2>
            {isLoading && <p className="g-muted">{t('commun.chargement')}</p>}
            {!isLoading && paths.length === 0 && <p className="g-muted">{t('carte.aucun_chemin')}</p>}
            <ul className="g-editor__list">
              {visiblePaths.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="g-editor__item"
                    onClick={() => {
                      selectPath(p)
                      flyTo(p.coordinates)
                    }}
                  >
                    <span className="g-editor__swatch" style={{ background: pathColor(p.path_type) }} aria-hidden />
                    <span className="g-editor__item-text">
                      <span>
                        {p.access === 'private' && <span className="mdi mdi-lock-outline g-editor__lock" title={t('carte.acces_private')} aria-label={t('carte.acces_private')} />}
                        {p.name || p.path_type_label}
                      </span>
                      <span className="g-muted">
                        {p.path_type_label} · {formatLength(lengthM(p.coordinates))}
                        {p.usages.length > 0 && ` · ${p.usages.map((u) => usageLabel(u)).join(', ')}`}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <h2 className="g-editor__section">
              {t('carte.equipements')} <span className="g-chip__count">{facilities.length}</span>
            </h2>
            {!isLoading && facilities.length === 0 && <p className="g-muted">{t('carte.aucun_equipement')}</p>}
            <ul className="g-editor__list">
              {visibleFacilities.map((f) => (
                <li key={f.id}>
                  <button
                    type="button"
                    className="g-editor__item"
                    onClick={() => {
                      selectFacility(f)
                      if (f.location.lat != null && f.location.lng != null) flyTo([{ lat: f.location.lat, lng: f.location.lng }])
                    }}
                  >
                    <span className={`mdi ${poiIcon(f.poi_type)} g-editor__pin`} style={{ color: f.poi_type?.icon_color || undefined }} aria-hidden />
                    <span className="g-editor__item-text">
                      <span>
                        {f.access === 'private' && <span className="mdi mdi-lock-outline g-editor__lock" title={t('carte.acces_private')} aria-label={t('carte.acces_private')} />}
                        {f.name || f.type_label || f.facility_type_label}
                      </span>
                      <span className="g-muted">{f.type_label || f.facility_type_label}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <h2 className="g-editor__section">
              {t('carte.sentiers_associes')} <span className="g-chip__count">{linkedTrails?.features.length ?? 0}</span>
            </h2>
            <p className="g-hint">{t('carte.aide_sentiers_associes')}</p>
            <ul className="g-editor__list">
              {visibleLinked.map((f) => (
                <li key={f.properties.id}>
                  <button
                    type="button"
                    className="g-editor__item"
                    onClick={() => {
                      void openTrail({ trailId: f.properties.id })
                      flyTo(lineCoords(f.geometry))
                    }}
                  >
                    <span className="g-editor__swatch g-editor__swatch--linked" aria-hidden />
                    <span className="g-editor__item-text">
                      <span>{f.properties.name || t('carte.sans_nom')}</span>
                      <span className="g-muted">
                        {[f.properties.track_label, f.properties.activities.join(', '), f.properties.length_m != null ? formatLength(f.properties.length_m) : '']
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {selection?.kind === 'poi' &&
          (() => {
            const poi = pois.find((x) => x.id === selection.id)
            if (!poi) return null
            const fiche = (
              <PoiPanel
                poi={poi}
                categories={poiCategories?.categories ?? []}
                busy={saving}
                onBack={clearSelection}
                onSave={(data) => void savePoi(poi.id, data)}
                onConvert={() => void convertPoiToFacility(poi.id)}
                onDelete={() => void removePoi(poi.id)}
              />
            )
            return poi.poi_type?.slug === 'climbing' ? <ClimbingPanel editor={climbing} fiche={fiche} onFocus={flyTo} /> : fiche
          })()}
        {selection?.kind === 'place' && <PlacePanel orgId={orgId} place={selection.place} onBack={clearSelection} />}
        {selection?.kind === 'newPlace' && (
          <NewPlacePanel
            kind={selection.type}
            busy={saving}
            onCancel={clearSelection}
            onCreate={(name, openEditor) => void createPlace(selection.type, selection.at, name, openEditor)}
          />
        )}

        {selection?.kind === 'trail' && (
          <TrailPanel
            feature={selection.feature}
            busy={trailBusy}
            cutting={mode === 'cut'}
            cutPoints={cutPoints.length}
            onBack={clearSelection}
            onLink={() => void setTrailLink(true)}
            onUnlink={() => void setTrailLink(false)}
            onCopyWhole={() => copyTrail(selection.feature, lineCoords(selection.feature.geometry))}
            onStartCut={() => {
              setCutPoints([])
              setMode('cut')
            }}
            onCancelCut={() => {
              setCutPoints([])
              setMode('select')
            }}
          />
        )}

        {selection?.kind === 'path' && (
          <div className="g-editor__details">
            <button type="button" className="g-back" onClick={() => confirmDiscard() && clearSelection()}>
              <span className="mdi mdi-arrow-left" aria-hidden /> {t('carte.retour_liste')}
            </button>
            <h2 className="g-editor__section">{selection.id === 'new' ? t('carte.nouveau_chemin') : selectedPath?.name || typeLabel(pathForm.path_type)}</h2>
            <p className="g-editor__length">
              <span className="mdi mdi-ruler" aria-hidden /> {formatLength(lengthM(geom))} · {t('carte.n_points', { count: geom.length })}
            </p>
            <PathFields
              form={pathForm}
              onChange={(update) => {
                setPathForm(update)
                setDirty(true)
              }}
              pathTypes={pathTypes}
              pathUsages={pathUsages}
            />
            {activeVertex !== null && (
              <div className="g-editor__vertex">
                <span>{t('carte.point_n', { n: activeVertex + 1 })}</span>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={geom.length <= 2}
                  onClick={() => {
                    changeGeom(geom.filter((_, i) => i !== activeVertex))
                    setActiveVertex(null)
                  }}
                >
                  <span className="mdi mdi-close" aria-hidden /> {t('carte.supprimer_point')}
                </button>
                {selection.id !== 'new' && activeVertex > 0 && activeVertex < geom.length - 1 && (
                  <button type="button" className="btn btn--sm" disabled={saving || dirty} title={dirty ? t('carte.enregistrer_avant_couper') : undefined} onClick={() => void splitAt(activeVertex)}>
                    <span className="mdi mdi-content-cut" aria-hidden /> {t('carte.couper_ici')}
                  </button>
                )}
              </div>
            )}
            <p className="g-hint">{t('carte.aide_modifier')}</p>
            <div className="g-editor__buttons">
              <button type="button" className="btn btn--primary" disabled={saving || !dirty || geom.length < 2} onClick={() => void savePath()}>
                {saving ? t('commun.enregistrement') : t('commun.enregistrer')}
              </button>
              <button type="button" className="btn" disabled={!history.length} onClick={undo} title="Ctrl+Z">
                <span className="mdi mdi-undo" aria-hidden /> {t('carte.annuler')}
              </button>
              <span className="g-spacer" />
              <button type="button" className="btn g-danger" disabled={saving} onClick={() => void removePath()}>
                <span className="mdi mdi-trash-can-outline" aria-hidden /> {t('commun.supprimer')}
              </button>
            </div>
          </div>
        )}

        {selection?.kind === 'facility' && (
          <div className="g-editor__details">
            <button type="button" className="g-back" onClick={() => confirmDiscard() && clearSelection()}>
              <span className="mdi mdi-arrow-left" aria-hidden /> {t('carte.retour_liste')}
            </button>
            <h2 className="g-editor__section">
              {selection.id === 'new' ? t('carte.nouvel_equipement') : selectedFacility?.name || selectedFacility?.type_label}
            </h2>
            {selection.id !== 'new' && <KindSwitch value="facility" disabled={saving || dirty} onChange={() => void convertFacilityToPoi()} />}
            <label className="g-field">
              <span className="g-field__label">{t('carte.type')}</span>
              {poiCategories?.categories.length ? (
                <select
                  className="g-input"
                  value={facilityForm.poi_type_id ?? ''}
                  onChange={(e) => {
                    setFacilityForm({ ...facilityForm, poi_type_id: e.target.value ? Number(e.target.value) : null })
                    setDirty(true)
                  }}
                >
                  {!facilityForm.poi_type_id && <option value="">{selectedFacility?.type_label || t('carte.choisir_type')}</option>}
                  {poiCategories.categories.map((c) => (
                    <optgroup key={c.id} label={c.name}>
                      {(c.children ?? []).map((child) => (
                        <option key={child.id} value={child.id}>
                          {child.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              ) : (
                <select
                  className="g-input"
                  value={facilityForm.facility_type}
                  onChange={(e) => {
                    setFacilityForm({ ...facilityForm, facility_type: e.target.value })
                    setDirty(true)
                  }}
                >
                  {facilityTypes.map((ft) => (
                    <option key={ft.value} value={ft.value}>
                      {ft.label}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label className="g-field">
              <span className="g-field__label">{t('carte.nom')}</span>
              <input
                className="g-input"
                value={facilityForm.name}
                onChange={(e) => {
                  setFacilityForm({ ...facilityForm, name: e.target.value })
                  setDirty(true)
                }}
              />
            </label>
            <label className="g-field">
              <span className="g-field__label">{t('carte.description')}</span>
              <textarea
                className="g-input"
                rows={3}
                value={facilityForm.info_text}
                placeholder={t('carte.description_exemple')}
                onChange={(e) => {
                  setFacilityForm({ ...facilityForm, info_text: e.target.value })
                  setDirty(true)
                }}
              />
              <span className="g-hint">{t('carte.aide_description')}</span>
            </label>
            <div className="g-field">
              <span className="g-field__label">{t('carte.acces')}</span>
              <div className="g-segmented g-segmented--full" role="radiogroup" aria-label={t('carte.acces')}>
                {(['public', 'private'] as const).map((a) => (
                  <button
                    key={a}
                    type="button"
                    role="radio"
                    aria-checked={facilityForm.access === a}
                    className={`g-segmented__item${facilityForm.access === a ? ' g-segmented__item--active' : ''}`}
                    onClick={() => {
                      setFacilityForm({ ...facilityForm, access: a })
                      setDirty(true)
                    }}
                  >
                    <span className={`mdi ${a === 'private' ? 'mdi-lock-outline' : 'mdi-earth'}`} aria-hidden /> {t(`carte.acces_${a}`)}
                  </button>
                ))}
              </div>
              <span className="g-hint">{facilityForm.access === 'private' ? t('carte.aide_equipement_prive') : t('carte.aide_equipement_public')}</span>
            </div>
            <label className="g-field">
              <span className="g-field__label">{t('carte.notes')}</span>
              <textarea
                className="g-input"
                rows={3}
                value={facilityForm.notes}
                onChange={(e) => {
                  setFacilityForm({ ...facilityForm, notes: e.target.value })
                  setDirty(true)
                }}
              />
            </label>
            <p className="g-hint">{t('carte.aide_equipement')}</p>
            <div className="g-editor__buttons">
              <button type="button" className="btn btn--primary" disabled={saving || !dirty || !facilityPoint} onClick={() => void saveFacility()}>
                {saving ? t('commun.enregistrement') : t('commun.enregistrer')}
              </button>
              <span className="g-spacer" />
              <button type="button" className="btn g-danger" disabled={saving} onClick={() => void removeFacility()}>
                <span className="mdi mdi-trash-can-outline" aria-hidden /> {t('commun.supprimer')}
              </button>
            </div>
          </div>
        )}
      </aside>

      <div className="g-editor__map">
        {error && <p className="g-alert g-alert--error g-editor__error">{(error as Error).message}</p>}
        <div className="g-editor__tools" role="toolbar" aria-label={t('carte.outils')}>
          <button type="button" className={`g-tool${mode === 'select' ? ' g-tool--active' : ''}`} onClick={() => startTool('select')} title={t('carte.outil_selection')}>
            <span className="mdi mdi-cursor-default-outline" aria-hidden />
            <span>{t('carte.outil_selection')}</span>
          </button>
          <button type="button" className={`g-tool${mode === 'draw' ? ' g-tool--active' : ''}`} onClick={() => startTool('draw')} title={t('carte.outil_chemin')}>
            <span className="mdi mdi-vector-polyline-plus" aria-hidden />
            <span>{t('carte.outil_chemin')}</span>
          </button>
          <button type="button" className={`g-tool${mode === 'facility' ? ' g-tool--active' : ''}`} onClick={() => startTool('facility')} title={t('carte.outil_equipement')}>
            <span className="mdi mdi-map-marker-plus-outline" aria-hidden />
            <span>{t('carte.outil_equipement')}</span>
          </button>
        </div>

        <div className="g-layers">
          <button
            type="button"
            className="g-layers__button g-layers__switch"
            onClick={() => setSatellite((v) => !v)}
            title={satellite ? t('carte.topographique') : t('carte.satellite')}
          >
            <span className={`mdi ${satellite ? 'mdi-terrain' : 'mdi-satellite-variant'}`} aria-hidden />
            <span>{satellite ? t('carte.topographique') : t('carte.satellite')}</span>
          </button>
          <button
            type="button"
            className={`g-layers__button${layersOpen ? ' g-layers__button--open' : ''}`}
            onClick={() => setLayersOpen((v) => !v)}
            aria-expanded={layersOpen}
            title={t('carte.calques')}
          >
            <span className="mdi mdi-layers-outline" aria-hidden />
            <span>{t('carte.calques')}</span>
          </button>
          {layersOpen && (
            <div className="g-layers__panel" role="dialog" aria-label={t('carte.calques')}>
              <div className="g-layers__title">{t('carte.fond_de_carte')}</div>
              <div className="g-layers__bases">
                {([false, true] as const).map((sat) => (
                  <button
                    key={String(sat)}
                    type="button"
                    className={`g-layers__base${satellite === sat ? ' g-layers__base--active' : ''}`}
                    onClick={() => setSatellite(sat)}
                    aria-pressed={satellite === sat}
                  >
                    <span className={`mdi ${sat ? 'mdi-satellite-variant' : 'mdi-terrain'}`} aria-hidden />
                    {sat ? t('carte.satellite') : t('carte.topographique')}
                  </button>
                ))}
              </div>
              <label className="g-layers__check">
                <input type="checkbox" checked={showPublicTrails} onChange={(e) => setShowPublicTrails(e.target.checked)} />
                <span>
                  <strong>{t('carte.sentiers_publics')}</strong>
                  <span className="g-hint">{t('carte.aide_sentiers_publics')}</span>
                </span>
              </label>
            </div>
          )}
        </div>

        {mode === 'draw' && gpx && <GpxImportCard draft={gpx} onChange={setGpx} onUse={applyGpx} onCancel={() => setGpx(null)} />}
        {mode === 'draw' && !gpx && (
          <div className="g-editor__banner">
            <span>{draft.length === 0 ? t('carte.aide_tracer_debut') : t('carte.aide_tracer', { count: draft.length, length: formatLength(lengthM(draft)) })}</span>
            {draft.length === 0 && (
              <>
                <input
                  ref={gpxInput}
                  type="file"
                  accept=".gpx,application/gpx+xml"
                  hidden
                  onChange={(e) => {
                    void openGpx(e.target.files?.[0])
                    e.target.value = ''
                  }}
                />
                <button type="button" className="btn btn--sm" onClick={() => gpxInput.current?.click()}>
                  <span className="mdi mdi-file-upload-outline" aria-hidden /> {t('carte.gpx_importer')}
                </button>
              </>
            )}
            <button type="button" className="btn btn--sm btn--primary" disabled={draft.length < 2} onClick={finishDraft}>
              {t('carte.terminer')}
            </button>
            <button type="button" className="btn btn--sm" onClick={cancelAll}>
              {t('commun.annuler')}
            </button>
          </div>
        )}
        {mode === 'cut' && (
          <div className="g-editor__banner">
            <span>{cutPoints.length === 0 ? t('carte.aide_decoupe_1') : t('carte.aide_decoupe_2')}</span>
            <button type="button" className="btn btn--sm" onClick={cancelAll}>
              {t('commun.annuler')}
            </button>
          </div>
        )}
        {mode === 'facility' && (
          <div className="g-editor__banner">
            <span>
              {placeKind
                ? t('carte.aide_placer_lieu', { name: t(`types.${placeKind}`) })
                : placeType
                  ? t(placeAs === 'poi' ? 'carte.aide_placer_poi' : 'carte.aide_placer_type', { name: placeType.name })
                  : t('carte.aide_choisir_type')}
            </span>
            <button type="button" className="btn btn--sm" onClick={cancelAll}>
              {t('commun.annuler')}
            </button>
          </div>
        )}
        {message && (
          <div className={`g-editor__toast g-editor__toast--${message.kind}`}>
            {message.text}
            {message.undo && (
              <button type="button" className="g-editor__toast-undo" onClick={message.undo}>
                {t('commun.annuler')}
              </button>
            )}
          </div>
        )}
        {mode === 'facility' && poiCategories && (
          <PoiPalette
            categories={poiCategories.categories}
            orgCounts={orgPoiCounts}
            selectedId={placeType?.id ?? null}
            onSelect={(type) => {
              setPlaceKind(null)
              setPlaceType(type)
            }}
            placeAs={placeAs}
            onPlaceAs={setPlaceAs}
            selectedKind={placeKind}
            onSelectKind={(kind) => {
              setPlaceType(null)
              setPlaceKind(kind)
            }}
            onClose={cancelAll}
          />
        )}

        <BaseMap
          ref={mapRef}
          basemapContext="map"
          mapBackground={satellite ? 'satellite' : 'outdoor-bright'}
          constrainToRegion={false}
          initialViewState={{ longitude: -71.2, latitude: 46.8, zoom: 7 }}
          style={{ width: '100%', height: '100%' }}
          doubleClickZoom={mode !== 'draw'}
          cursor={mode === 'select' && !climbing.tool ? undefined : 'crosshair'}
          interactiveLayerIds={['org-paths-hit', 'org-linked-hit']}
          onClick={onMapClick}
          onDblClick={(e) => {
            if (mode === 'draw') {
              e.preventDefault()
              finishDraft()
            }
          }}
          onLoad={() => {
            if (pendingFocus.current) {
              flyTo(pendingFocus.current)
              pendingFocus.current = null
            } else if (bundle && !fitted.current) {
              fitted.current = true
              fitToContent(bundle)
            }
          }}
        >
          <MartinTrailLayers enabled={showPublicTrails} selection={ALL_TRAILS} />
          <ClimbingEditLayers editor={climbing} />
          {mode === 'draw' && gpx && <GpxPreviewLayers draft={gpx} />}
          {boundary && (
            <Source id="org-boundary" type="geojson" data={{ type: 'Feature', properties: {}, geometry: boundary }}>
              <Layer id="org-boundary-fill" type="fill" paint={{ 'fill-color': '#954A00', 'fill-opacity': 0.06 }} />
              <Layer id="org-boundary-line" type="line" paint={{ 'line-color': '#954A00', 'line-width': 2, 'line-dasharray': [3, 2] }} />
            </Source>
          )}
          {linkedTrails && (
            <Source id="org-linked" type="geojson" data={linkedTrails}>
              <Layer id="org-linked-halo" type="line" layout={{ 'line-cap': 'round', 'line-join': 'round' }} paint={{ 'line-color': LINKED_COLOR, 'line-width': 7, 'line-opacity': 0.35 }} />
              <Layer id="org-linked-line" type="line" layout={{ 'line-cap': 'round', 'line-join': 'round' }} paint={{ 'line-color': LINKED_COLOR, 'line-width': 2.5 }} />
              <Layer id="org-linked-hit" type="line" paint={{ 'line-color': '#000000', 'line-width': 14, 'line-opacity': 0 }} />
            </Source>
          )}
          <Source id="org-trail-selected" type="geojson" data={selectedTrailGeoJson}>
            <Layer id="org-trail-selected-halo" type="line" layout={{ 'line-cap': 'round', 'line-join': 'round' }} paint={{ 'line-color': '#ffd54f', 'line-width': 10, 'line-opacity': 0.8 }} />
            <Layer id="org-trail-selected-line" type="line" layout={{ 'line-cap': 'round', 'line-join': 'round' }} paint={{ 'line-color': LINKED_COLOR, 'line-width': 3 }} />
          </Source>
          <Source id="org-paths" type="geojson" data={pathsGeoJson}>
            <Layer id="org-paths-casing" type="line" layout={{ 'line-cap': 'round', 'line-join': 'round' }} paint={{ 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.9 }} />
            <Layer
              id="org-paths-line"
              type="line"
              filter={['!', ['get', 'dashed']]}
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{ 'line-color': ['get', 'color'], 'line-width': 4 }}
            />
            <Layer
              id="org-paths-private"
              type="line"
              filter={['get', 'dashed']}
              layout={{ 'line-join': 'round' }}
              paint={{ 'line-color': ['get', 'color'], 'line-width': 4, 'line-dasharray': [2, 1.5] }}
            />
            <Layer id="org-paths-hit" type="line" paint={{ 'line-color': '#000000', 'line-width': 16, 'line-opacity': 0 }} />
          </Source>
          <Source id="org-edit" type="geojson" data={editGeoJson}>
            <Layer id="org-edit-halo" type="line" layout={{ 'line-cap': 'round', 'line-join': 'round' }} paint={{ 'line-color': '#ffd54f', 'line-width': 11, 'line-opacity': 0.7 }} />
            <Layer id="org-edit-line" type="line" layout={{ 'line-cap': 'round', 'line-join': 'round' }} paint={{ 'line-color': ['get', 'color'], 'line-width': 4 }} />
          </Source>
          <Source id="org-nodes" type="geojson" data={nodesGeoJson}>
            <Layer
              id="org-nodes"
              type="circle"
              minzoom={13}
              paint={{
                'circle-radius': ['match', ['get', 'role'], 'vertex', 3, 4.5],
                'circle-color': ['match', ['get', 'role'], 'junction', '#424242', 'end', '#d32f2f', '#ffffff'],
                'circle-stroke-color': ['match', ['get', 'role'], 'vertex', '#616161', '#ffffff'],
                'circle-stroke-width': 1.5,
              }}
            />
          </Source>
          <Source id="org-draft-points" type="geojson" data={draftPoints}>
            <Layer
              id="org-draft-points"
              type="circle"
              paint={{
                'circle-radius': ['case', ['get', 'first'], 6, 4],
                'circle-color': '#ffffff',
                'circle-stroke-color': '#1b5e20',
                'circle-stroke-width': 2,
              }}
            />
          </Source>

          {selection?.kind === 'path' &&
            geom.map((c, i) => (
              <Marker
                key={`v-${i}`}
                longitude={c.lng}
                latitude={c.lat}
                draggable
                onClick={(e) => {
                  e.originalEvent.stopPropagation()
                  setActiveVertex(i)
                }}
                onDragEnd={(e) => {
                  const p = snap({ lat: e.lngLat.lat, lng: e.lngLat.lng }, { excludePathId: selection.id })
                  changeGeom(geom.map((x, j) => (j === i ? p : x)))
                  setActiveVertex(i)
                }}
              >
                <span
                  className={`g-vertex${activeVertex === i ? ' g-vertex--active' : ''}${i === 0 || i === geom.length - 1 ? ' g-vertex--end' : ''}${junctions.has(pointKey(c)) ? ' g-vertex--junction' : ''}`}
                />
              </Marker>
            ))}
          {candidates.map((c, i) => (
            <Marker
              key={`j-${i}-${c.point.lat}-${c.point.lng}`}
              longitude={c.point.lng}
              latitude={c.point.lat}
              onClick={(e) => {
                e.originalEvent.stopPropagation()
                void addJunction(c)
              }}
            >
              <span className="g-junction-add" title={t('carte.ajouter_jonction')}>
                <span className="mdi mdi-vector-link" aria-hidden />
              </span>
            </Marker>
          ))}
          {selection?.kind === 'path' &&
            geom.slice(1).map((c, i) => {
              const m = midpoint(geom[i], c)
              return (
                <Marker
                  key={`m-${i}`}
                  longitude={m.lng}
                  latitude={m.lat}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation()
                    changeGeom([...geom.slice(0, i + 1), m, ...geom.slice(i + 1)])
                    setActiveVertex(i + 1)
                  }}
                >
                  <span className="g-midpoint" title={t('carte.ajouter_point')}>
                    +
                  </span>
                </Marker>
              )
            })}

          {places
            .filter((pl) => PLACE_TYPES.has(pl.type) && pl.coordinates.lat && pl.coordinates.lng)
            .map((pl) => (
              <Marker
                key={`pl-${pl.type}-${pl.id}`}
                longitude={pl.coordinates.lng}
                latitude={pl.coordinates.lat}
                anchor="center"
                onClick={(e) => {
                  e.originalEvent.stopPropagation()
                  if (mode === 'select' && confirmDiscard()) {
                    clearSelection()
                    setSelection({ kind: 'place', place: pl })
                  }
                }}
              >
                <span className={`g-place-marker${selection?.kind === 'place' && selection.place.id === pl.id && selection.place.type === pl.type ? ' g-place-marker--active' : ''}`} title={pl.name}>
                  <TypeIcon type={pl.type} />
                </span>
              </Marker>
            ))}
          {pois
            .filter((poi) => poi.location)
            .map((poi) => (
              <Marker
                key={`poi-${poi.id}`}
                longitude={poi.location!.lng}
                latitude={poi.location!.lat}
                anchor="center"
                onClick={(e) => {
                  e.originalEvent.stopPropagation()
                  if (mode === 'select' && confirmDiscard()) {
                    clearSelection()
                    setSelection({ kind: 'poi', id: poi.id })
                  }
                }}
              >
                <span
                  className={`g-poi-marker${selection?.kind === 'poi' && selection.id === poi.id ? ' g-poi-marker--active' : ''}`}
                  style={{ background: poi.poi_type?.icon_color || undefined }}
                  title={poi.name}
                >
                  <span className={`mdi ${poiIcon(poi.poi_type)}`} aria-hidden />
                </span>
              </Marker>
            ))}
          {pitches.map((p) => (
            <Marker
              key={`pitch-${p.id}`}
              longitude={p.location.lng!}
              latitude={p.location.lat!}
              anchor="center"
              onClick={(e) => {
                e.originalEvent.stopPropagation()
                if (mode === 'select' && confirmDiscard()) navigate(`/o/${orgId}/lieux?onglet=emplacements`)
              }}
            >
              <span className="g-pitch-marker" title={[p.name, p.rate_tag_label || p.rate_tag].filter(Boolean).join(' · ') || t('carte.emplacement')}>
                <span className="mdi mdi-tent" aria-hidden />
                {p.name && <span className="g-pitch-marker__label">{p.name}</span>}
              </span>
            </Marker>
          ))}
          {selection?.kind === 'newPlace' && (
            <Marker longitude={selection.at.lng} latitude={selection.at.lat} anchor="center">
              <span className="g-place-marker g-place-marker--active">
                <TypeIcon type={selection.type} />
              </span>
            </Marker>
          )}
          {cutPoints.map((c, i) => (
            <Marker key={`cut-${i}`} longitude={c.lng} latitude={c.lat}>
              <span className="g-vertex g-vertex--active" />
            </Marker>
          ))}
          {facilities.map((f) => {
            const isSel = selection?.kind === 'facility' && selection.id === f.id
            const pos = isSel ? facilityPoint : f.location.lat != null && f.location.lng != null ? { lat: f.location.lat, lng: f.location.lng } : null
            if (!pos) return null
            return (
              <Marker
                key={`f-${f.id}`}
                longitude={pos.lng}
                latitude={pos.lat}
                anchor="bottom"
                draggable={isSel}
                onClick={(e) => {
                  e.originalEvent.stopPropagation()
                  if (mode === 'select' && !isSel) selectFacility(f)
                }}
                onDragEnd={(e) => {
                  setFacilityPoint({ lat: e.lngLat.lat, lng: e.lngLat.lng })
                  setDirty(true)
                }}
              >
                <span className={`g-pin${isSel ? ' g-pin--active' : ''}${f.access === 'private' ? ' g-pin--private' : ''}`} title={f.name || f.type_label || ''}>
                  <span className="mdi mdi-map-marker" aria-hidden style={{ color: f.poi_type?.icon_color || undefined }} />
                  {f.poi_type && <span className={`mdi ${poiIcon(f.poi_type)} g-pin__icon`} aria-hidden />}
                </span>
              </Marker>
            )
          })}
          {selection?.kind === 'facility' && selection.id === 'new' && facilityPoint && (
            <Marker
              longitude={facilityPoint.lng}
              latitude={facilityPoint.lat}
              anchor="bottom"
              draggable
              onDragEnd={(e) => setFacilityPoint({ lat: e.lngLat.lat, lng: e.lngLat.lng })}
            >
              <span className="g-pin g-pin--active">
                <span className="mdi mdi-map-marker-plus" aria-hidden />
              </span>
            </Marker>
          )}
        </BaseMap>
      </div>
    </div>
  )
}
